import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import OntruckLogo from 'assets/icons/OntruckLogo';
import { Button } from '@ontruck/orc';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'utils/constants/routes';
import { useAuth } from 'hooks/useAuth/useAuth';
import { usePendingPods } from 'hooks/usePendingPods/usePendingPods';
import Menu from 'assets/icons/Menu';
import Dropdown from 'components/Dropdown';
import cx from 'classnames';
import {
  FALLBACK_LANG,
  FAQS_URLS,
  LOCALES_MATCHES,
} from 'utils/constants/locales';
import { getLocalesMatcher } from 'utils/functions/locales';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import useDocsCarrierProfile from 'hooks/useDocsCarrierProfile/useDocsCarrierProfile';
import { useMediaQuery } from 'react-responsive';
import { MEDIA_QUERIES } from 'utils/style/media';

const NavBarAccountMenu = ({
  handleGoToAccountSettings,
  handleGoToDocumentation,
  handleLogOut,
  isDesktop,
}) => {
  const auth = useAuth();
  const { t } = useTranslation();

  return (
    <ul
      className="navbar__user-dropdown"
      data-testid={`${
        isDesktop
          ? 'navbar__user-dropdown--desktop'
          : 'navbar__user-dropdown--mobile'
      }`}
    >
      <li className="navbar__user-dropdown-item">
        <Button
          className="navbar__user-dropdown-button"
          nude
          skin="primary"
          onClick={handleGoToAccountSettings}
          text={t('AccountSettings:accountSettings')}
          data-testid="navbar__account-button--settings"
        />
      </li>
      {auth.profile?.carrier?.country !== 'NL' && (
        <li className="navbar__user-dropdown-item">
          <Button
            className="navbar__user-dropdown-button"
            nude
            skin="primary"
            onClick={handleGoToDocumentation}
            text={t('Documents:documentation')}
            data-testid="navbar__account-button--documents"
          />
        </li>
      )}
      {isDesktop && (
        <li className="navbar__user-dropdown-item">
          <Button
            className="navbar__user-dropdown-button"
            nude
            skin="primary"
            onClick={handleLogOut}
            text={t('Login:logout')}
            data-testid="navbar__logout_button--desktop"
          />
        </li>
      )}
    </ul>
  );
};

const NavBar = ({ className }) => {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const [isOpenMobile, setIsOpenMobile] = useState(false);
  const [userDropOpen, setUserDropOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const { numberOfPendingPods, showDot } = usePendingPods();
  const { clearCarrierProfile } = useDocsCarrierProfile();
  const menuRef = useRef(null);
  const menuOpenerRef = useRef(null);
  const isDesktop = useMediaQuery({
    query: MEDIA_QUERIES.tablet,
  });

  const closeMobileMenu = useCallback(() => {
    setIsOpenMobile(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !menuOpenerRef.current?.contains(event.target)
      ) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef, closeMobileMenu]);

  const handleOpener = useCallback(() => {
    setIsOpenMobile(!isOpenMobile);
  }, [isOpenMobile, setIsOpenMobile]);

  const handleUserDropdown = useCallback(() => {
    setUserDropOpen(!userDropOpen);
  }, [userDropOpen]);

  const closeUserDropdown = useCallback(() => {
    setUserDropOpen(false);
  }, []);

  const handleLogOut = useCallback(() => {
    setUserDropOpen(false);
    auth.signout();
    clearCarrierProfile();
    closeMobileMenu();
    history.push(ROUTES.LOGIN);
  }, [auth, history, clearCarrierProfile, closeMobileMenu]);

  const handleGoToAccountSettings = useCallback(() => {
    setUserDropOpen(false);
    closeMobileMenu();
    history.push(ROUTES.ACCOUNT_SETTINGS);
  }, [history, closeMobileMenu]);

  const handleGoToDocumentation = useCallback(() => {
    setUserDropOpen(false);
    closeMobileMenu();
    history.push(`${ROUTES.DOCS.DEFAULT}/${auth.profile?.carrier?.uuid}`);
  }, [history, auth, closeMobileMenu]);

  const handleRedirectToFaqs = useCallback(() => {
    const selectedLanguage = getLocalesMatcher(i18n.language, LOCALES_MATCHES);
    const destinationURL =
      FAQS_URLS[selectedLanguage] || FAQS_URLS[FALLBACK_LANG.toLowerCase()];
    sendEventToSegment(TRACKING_EVENTS.NAVBAR_HELP_LINK_CLICKED, {
      destinationURL: destinationURL,
      userId: auth.profile?.user?.id,
    });
    window.open(destinationURL, '_tab');
  }, [i18n, auth]);

  const isLoginRoute = location.pathname === ROUTES.LOGIN;
  const isDocsPage = location.pathname.includes(ROUTES.DOCS.DEFAULT);

  return (
    <div className={className}>
      <div
        className={cx('navbar', { 'navbar--login': isLoginRoute })}
        data-testid="navbar"
      >
        {!isLoginRoute && (
          <div className="navbar__brand">
            <Link to={ROUTES.HOME}>
              <OntruckLogo />
            </Link>
          </div>
        )}
        {auth.profile && !isLoginRoute && (
          <>
            <button
              className="navbar__opener"
              onClick={handleOpener}
              data-testid="navbar__opener"
              ref={menuOpenerRef}
            >
              <Menu />
            </button>
            <div
              className={`navbar__collapse ${
                isOpenMobile ? 'navbar__collapse--open' : ''
              }`}
              data-testid="navbar__collapse"
              ref={menuRef}
            >
              <nav className="navbar__nav">
                {!isDesktop && (
                  <NavBarAccountMenu
                    handleGoToAccountSettings={handleGoToAccountSettings}
                    handleGoToDocumentation={handleGoToDocumentation}
                    handleLogOut={handleLogOut}
                    isDesktop={isDesktop}
                  />
                )}
                <div className="navbar__nav-container">
                  <NavLink
                    className="navbar__link"
                    to={ROUTES.OFFERS}
                    onClick={closeMobileMenu}
                    activeClassName="navbar__link--active"
                  >
                    {t('Offers:Offers')}
                  </NavLink>
                  <NavLink
                    className="navbar__link"
                    to={ROUTES.PENDING}
                    activeClassName="navbar__link--active"
                    onClick={closeMobileMenu}
                  >
                    {t('Jobs:pending')}
                  </NavLink>
                  <NavLink
                    className="navbar__link"
                    to={ROUTES.IN_TRANSIT}
                    activeClassName="navbar__link--active"
                    onClick={closeMobileMenu}
                  >
                    {t('Jobs:inTransitNav')}
                  </NavLink>
                  <NavLink
                    className="navbar__link"
                    to={ROUTES.DONE}
                    activeClassName="navbar__link--active"
                    onClick={closeMobileMenu}
                  >
                    {t('Jobs:doneJobsNav')}
                  </NavLink>
                </div>
                {numberOfPendingPods > 0 && (
                  <div className="navbar__nav-container">
                    <NavLink
                      className={cx('navbar__link', {
                        'navbar__link--pod': showDot,
                      })}
                      to={ROUTES.PENDING_PODS}
                      activeClassName="navbar__link--active"
                      onClick={closeMobileMenu}
                    >
                      {`${t('Jobs:pendingPodNav')} (${numberOfPendingPods})`}
                    </NavLink>
                  </div>
                )}
                {!isDesktop && (
                  <div className="navbar__user-dropdown-item">
                    <Button
                      className="navbar__user-dropdown-button"
                      nude
                      skin="primary"
                      onClick={handleLogOut}
                      text={t('Login:logout')}
                      data-testid="navbar__logout_button--mobile"
                    />
                  </div>
                )}
              </nav>
              <div className="navbar__user-container">
                <Button
                  className="navbar__help-button"
                  data-testid="navbar__help-button"
                  nude
                  skin="secondary"
                  text={t('General:help')}
                  onClick={handleRedirectToFaqs}
                />
                <Button
                  className="navbar__dropdown-button"
                  nude
                  skin="secondary"
                  icon="arrowtip-down"
                  iconPosition="right"
                  onClick={handleUserDropdown}
                  text={t('General:yourAccount')}
                  data-testid="navbar__account-button"
                />
                {isDesktop && (
                  <Dropdown
                    show={!isDesktop ? true : userDropOpen}
                    onClose={closeUserDropdown}
                  >
                    {auth.profile?.driver?.contact_name && (
                      <div className="navbar__user-dropdown-header">
                        {auth.profile.driver.contact_name}
                      </div>
                    )}
                    <NavBarAccountMenu
                      handleGoToAccountSettings={handleGoToAccountSettings}
                      handleGoToDocumentation={handleGoToDocumentation}
                      handleLogOut={handleLogOut}
                      isDesktop={isDesktop}
                    />
                  </Dropdown>
                )}
              </div>
            </div>
          </>
        )}
        {!auth.profile && isDocsPage && (
          <a
            className="navbar__docs-help-link"
            href="mailto:documentacion@ontruck.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('Documents:helpLink')}
          </a>
        )}
      </div>
    </div>
  );
};

NavBar.propTypes = {
  className: PropTypes.string,
};

NavBar.defaultProps = {
  className: '',
};

export default NavBar;
